import { useState } from 'react';
import { Button, Grid, InlineStack, Panel, PanelSection, Paragraph } from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { useNavigationGuard } from '@/services/navigation-guard';
import { useHistoryLeaveBlocker } from '@/services/navigation-guard/use-history-leave-blocker';
import { useSqlImportDatabases } from '@/services/db/sql-import-database/queries';
import { SQL_DATABASE_CATALOG } from '@/features/sql-sync';
import { DatabaseStep } from '../../new/components/database-step';
import { SETTINGS_FORM_ID, SettingsStep } from '../../new/components/settings-step';
import { SQL_SERVER_IPS } from '../../new/database-form';
import { useDatabaseStep } from '../../new/use-database-step';
import { useSqlImportDetail } from '../context';
import { useDraftEdits } from '../use-draft-edits';
import { useSettingsSave } from './use-settings-save';
import { useSqlImportTabViewTracking } from '../use-tab-view-tracking';

const DATABASE_LOCKED_HINT =
  "An active sync can't move to another database; create a new sync to use a different one. Its connection details can still be edited.";

/**
 * The Settings tab: the import's schedule, target and identifiers on one side, its source database
 * on the other. Saving sends the whole import; a change to the identifier or the database asks the
 * customer to re-check the query afterwards, as Ember did.
 */
export function SettingsTab() {
  useSqlImportTabViewTracking('settingsTabViewed');

  const {
    workspaceId,
    type,
    sqlImportId,
    sqlImport,
    objectTypes,
    syncTarget,
    canEdit,
    canIdentifyByEmail,
    hrefs,
    isDeleted,
  } = useSqlImportDetail();
  const workspace = Number(workspaceId);
  const auth = useAuth();
  const { data: databases } = useSqlImportDatabases({ type, workspaceId: workspace });
  const edits = useDraftEdits(sqlImport);
  // The backend refuses a new database or identifier on an active (published) import; a draft may.
  const isTargetLocked = !sqlImport.is_draft;
  const databaseStep = useDatabaseStep({
    workspaceId: workspace,
    type,
    databaseTypeName: SQL_DATABASE_CATALOG[type].name,
    syncTarget,
    databases,
    selectedId: edits.draft.database_id,
    onSelect: (id) => {
      if (!isTargetLocked) {
        edits.setDraftFields({ database_id: id });
      }
    },
  });
  // Discarding remounts the form, so its fields show the baseline again and not the dropped edits.
  const [formVersion, setFormVersion] = useState(0);
  const { isSaving, saveAndContinue, onSubmit, onInvalid } = useSettingsSave({
    workspaceId: workspace,
    type,
    sqlImportId,
    sqlImport,
    edits,
    databaseStep,
    queryHref: hrefs.query,
  });

  const discard = () => {
    edits.reset();
    databaseStep.cancelEdit();
    setFormVersion((version) => version + 1);
  };
  const prompt = {
    hasUnsavedChanges: () => !isDeleted() && edits.hasUnsavedChanges(),
    onSave: canEdit ? saveAndContinue : undefined,
    isSaveDisabled: !canEdit || isSaving,
    onDiscard: discard,
  };
  useNavigationGuard(prompt);
  useHistoryLeaveBlocker(prompt);

  return (
    <>
      <InlineStack alignInline="end" mb="200">
        <Button
          type="submit"
          form={SETTINGS_FORM_ID}
          variant="primary"
          isDisabled={!canEdit || !edits.isDirty}
          isLoading={isSaving}
          data-test-sql-import-button="submit"
        >
          Save changes
        </Button>
      </InlineStack>
      <Grid columns={2} gap="200" alignItems="flex-start">
        <Panel>
          <PanelSection>
            <SettingsStep
              key={formVersion}
              workspaceId={workspace}
              draft={edits.draft}
              objectTypes={objectTypes}
              canIdentifyByEmail={canIdentifyByEmail}
              canCreateSegment={false}
              isReadOnly={!canEdit}
              onChange={edits.setDraftFields}
              onSubmit={onSubmit}
              onInvalid={onInvalid}
              isTargetLocked={isTargetLocked}
            />
          </PanelSection>
        </Panel>
        <Panel>
          <PanelSection>
            {isTargetLocked ? (
              <Paragraph color="subtle" mb="200" data-test-sql-import-database-locked>
                {DATABASE_LOCKED_HINT}
              </Paragraph>
            ) : null}
            <DatabaseStep
              type={type}
              databases={databases}
              isReadOnly={!canEdit}
              sqlServerIp={SQL_SERVER_IPS[auth.account.data_center]}
              state={databaseStep}
            />
          </PanelSection>
        </Panel>
      </Grid>
    </>
  );
}
